import { TDocumentType } from '@/@types/document-type';
import { MoneyInput, Modal, parseMoneyInputValue } from '@/components/common';
import { NotePresetSelector } from '@/components/notes';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import * as zod from 'zod';
import { ModalTitle } from '../common/Modal/common/ModalTitle';
import {
  manualCashTransactionTypes,
  transactionTypeLabel,
} from './cash-management.utils';

const manualMovementSchema = zod.object({
  type: zod.enum(['CASH_IN', 'CASH_OUT', 'PAY_IN', 'PAY_OUT']),
  documentTypeId: zod.string().min(1, 'Informe o tipo de documento'),
  amount: zod
    .number({
      required_error: 'Informe o valor',
      invalid_type_error: 'Informe um valor válido',
    })
    .min(0.01, 'Informe um valor maior que zero'),
  description: zod
    .string()
    .min(1, 'Informe a descrição')
    .transform((description) => description.trim().toUpperCase()),
});

export type ManualMovementFormData = zod.infer<typeof manualMovementSchema>;

const lockedDocumentTypeMovementTypes: ManualMovementFormData['type'][] = [
  'CASH_IN',
  'CASH_OUT',
];

type ManualMovementModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  documentTypes: TDocumentType[];
  defaultDocumentTypeId?: string | null;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (data: ManualMovementFormData) => void;
};

export function ManualMovementModal({
  isOpen,
  isLoading,
  documentTypes,
  defaultDocumentTypeId,
  onClose,
  onSubmit,
}: ManualMovementModalProps) {
  const {
    control,
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    formState: { errors },
  } = useForm<ManualMovementFormData>({
    resolver: zodResolver(manualMovementSchema),
    defaultValues: {
      type: 'PAY_IN',
      documentTypeId: defaultDocumentTypeId || '',
      amount: 0,
      description: '',
    },
  });
  const selectedType = useWatch({ control, name: 'type' });
  const shouldLockDocumentType =
    lockedDocumentTypeMovementTypes.includes(selectedType);

  useEffect(() => {
    if (!isOpen) return;

    reset({
      type: 'PAY_IN',
      documentTypeId: defaultDocumentTypeId || '',
      amount: 0,
      description: '',
    });
  }, [defaultDocumentTypeId, isOpen, reset]);

  useEffect(() => {
    if (!isOpen || !shouldLockDocumentType) return;

    setValue('documentTypeId', defaultDocumentTypeId || '', {
      shouldValidate: true,
    });
  }, [defaultDocumentTypeId, isOpen, setValue, shouldLockDocumentType]);

  const handleFormSubmit = (data: ManualMovementFormData) => {
    if (lockedDocumentTypeMovementTypes.includes(data.type)) {
      if (!defaultDocumentTypeId) {
        setError('documentTypeId', {
          type: 'manual',
          message: 'Configure o tipo de documento padrão do caixa',
        });
        return;
      }

      onSubmit({
        ...data,
        documentTypeId: defaultDocumentTypeId,
      });
      return;
    }

    onSubmit({
      ...data,
    });
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      closeOnBackdropClick={false}
    >
      {isLoading ? <LinearProgress /> : null}
      <ModalTitle title="Movimentar Caixa" onClose={onClose} />
      <DialogContent dividers>
        <Box
          component="form"
          id="manual-movement-form"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <Stack spacing={2}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Tipo"
                  error={!!errors.type}
                  helperText={errors.type?.message}
                >
                  {manualCashTransactionTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {transactionTypeLabel[type]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Stack flexDirection={{ sm: 'row' }} gap={2}>
              <FormControl fullWidth>
                <Controller
                  name="documentTypeId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <Autocomplete
                      options={documentTypes}
                      getOptionLabel={(option) => option.name}
                      noOptionsText="Nenhum registro encontrado"
                      autoHighlight
                      value={
                        value
                          ? documentTypes.find(
                              (documentType) => documentType.id === value
                            ) || null
                          : null
                      }
                      isOptionEqualToValue={(option, selectedOption) =>
                        option.id === selectedOption.id
                      }
                      onChange={(_, newValue) =>
                        onChange(newValue ? newValue.id : '')
                      }
                      disabled={shouldLockDocumentType}
                      renderInput={(params) => (
                        <TextField
                          {...field}
                          {...params}
                          label="Tipo de documento"
                          error={!!errors.documentTypeId}
                          helperText={errors.documentTypeId?.message}
                        />
                      )}
                    />
                  )}
                />
              </FormControl>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <MoneyInput
                    {...field}
                    fullWidth
                    label="Valor"
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                    onChange={({ target }) =>
                      field.onChange(parseMoneyInputValue(target.value))
                    }
                  />
                )}
              />
            </Stack>
            <NotePresetSelector
              label="Descrição"
              minRows={3}
              error={!!errors.description}
              helperText={errors.description?.message}
              {...register('description')}
              type="CASH_SESSION"
              disabled={isLoading}
              onPresetSelect={(content) =>
                setValue('description', content, { shouldDirty: true })
              }
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        <Button disabled={isLoading} onClick={onClose}>
          Cancelar
        </Button>
        <Button
          type="submit"
          form="manual-movement-form"
          disabled={isLoading}
          variant="contained"
        >
          Salvar
        </Button>
      </DialogActions>
    </Modal>
  );
}
